import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { GraduationCap } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import inductionService from '../../services/induction';
import type { PackItem } from '../../types/induction';

/** Pages the prompt must not cover: the reading itself, and the signed-out pages. */
const HIDDEN_PREFIXES = ['/my-inductions', '/terms', '/privacy', '/login', '/register', '/reset-password', '/invite'];
const SNOOZE_KEY = 'induction-gate-later';

function isSnoozed(): boolean {
  try {
    return window.sessionStorage.getItem(SNOOZE_KEY) === '1';
  } catch {
    return false;
  }
}

function snooze() {
  try {
    window.sessionStorage.setItem(SNOOZE_KEY, '1');
  } catch {
    // Storage blocked: the in-memory flag still hides it for this mount.
  }
}

/**
 * Reminds a signed-in member that company policies in their induction pack are
 * waiting to be read. Mounted once in App, beside PlatformTermsGate.
 *
 * It is a prompt, never a lock: "Later" hides it for the browser session, and
 * nothing here stands between a worker and clock-in. It fails open — any error
 * from the check and nothing is shown.
 */
export default function InductionGate() {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(isSnoozed);
  const orgId = user?.organisation_id;

  const { data, isError } = useQuery({
    queryKey: ['inductions', 'mine', user?.id, orgId],
    queryFn: () => inductionService.mine(),
    enabled: isAuthenticated && !!user && !!orgId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!isAuthenticated || !orgId || dismissed || isError || !data) return null;
  if (pathname === '/' || HIDDEN_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const outstanding = (data.pack ?? []).filter((p) => p.status !== 'acknowledged');
  if (outstanding.length === 0) return null;

  return (
    <InductionPromptDialog
      items={outstanding}
      onRead={() => navigate(`/my-inductions/policies/${outstanding[0].id}`)}
      onLater={() => {
        snooze();
        setDismissed(true);
      }}
    />
  );
}

interface DialogProps {
  items: PackItem[];
  onRead: () => void;
  onLater: () => void;
}

export function InductionPromptDialog({ items, onRead, onLater }: DialogProps) {
  return (
    <div
      className="fixed inset-0 z-[55] bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="induction-gate-title"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col">
        <header className="px-4 sm:px-6 py-4 border-b border-neutral-200 flex items-start gap-3">
          <GraduationCap className="h-5 w-5 text-primary-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h2 id="induction-gate-title" className="text-lg font-semibold text-neutral-900 break-words">
              Your induction — before you start
            </h2>
            <p className="text-sm text-neutral-600">
              {items.length === 1
                ? 'One company policy is waiting for you to read and acknowledge.'
                : `${items.length} company policies are waiting for you to read and acknowledge.`}
            </p>
          </div>
        </header>

        <ul className="flex-1 overflow-y-auto px-4 sm:px-6 py-3 space-y-2">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
              <span className="min-w-0 break-words text-neutral-800">{item.title}</span>
              {item.status === 'updated' && (
                <span className="rounded-full bg-warning-50 px-2 py-0.5 text-xs font-medium text-warning-700">Updated</span>
              )}
            </li>
          ))}
        </ul>

        <footer className="px-4 sm:px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl">
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={onLater}>Later</Button>
            <Button onClick={onRead}>Read and acknowledge</Button>
          </div>
        </footer>
      </div>
    </div>
  );
}
